'use client'
// =============================================================================
// ProjectAiTab — le panneau « IA du projet » (CRM-PROJECT-ERP-MODEL.md §20.23).
//
// Il ne PARLE que de ce que le projet a réellement mesuré (explain.ts) : explications
// tirées des faits, jamais inventées, et il le dit. Poser une question ne modifie rien.
// Une action nécessaire est PROPOSÉE ; elle ne part qu'après un clic de validation
// ET la confirmation de ce qu'elle fait — puis le panneau VÉRIFIE le résultat sur les
// faits relus (proposition → validation → exécution → vérification).
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePageContext } from '../../../../stores/page-context.store'
import {
  SUGGESTED, answer, factsSheet, intentOf, proposals, verification, type Answer, type Proposal, type ProjectFacts,
} from '../../../../src/lib/project-explain'

interface Message { from: 'user' | 'ai'; text?: string; answer?: Answer; /** État d'ouverture : relu sur les faits courants à chaque affichage. */ live?: boolean; llm?: boolean; usedUserFacts?: number; graph?: { nodes: number; edges: number; unverified: number }; note?: string }
interface ActionRun { id: Proposal['id']; title: string; since: number; error?: string }

const mono = 'var(--font-mono)'
const chip = (c: string) => ({ fontSize: 9, fontFamily: mono, padding: '2px 7px', borderRadius: 999, color: c, border: `1px solid ${c}55`, background: `${c}14` }) as const

export function ProjectAiTab() {
  const project = usePageContext(s => s.project)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [runs, setRuns] = useState<ActionRun[]>([])
  const [showFacts, setShowFacts] = useState(false)
  const [thinking, setThinking] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  const projectId = project?.facts.projectId

  // Nouvelle page de projet : la conversation repart de zéro, avec l'état du moment.
  useEffect(() => {
    setRuns([])
    if (!project) { setMessages([]); return }
    setMessages([{ from: 'ai', live: true, answer: answer('status', project.facts) }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }) }, [messages.length, runs.length])

  // « Vérification » : tant qu'une action est en cours, relire les faits (toutes les 4 s).
  const pending = useMemo(() => {
    if (!project) return false
    return runs.some(r => !r.error && !verification(r.id, project.facts, r.since).done)
  }, [runs, project])
  useEffect(() => {
    if (!pending || !project) return
    const t = setInterval(() => { project.refresh().catch(() => {}) }, 4000)
    return () => clearInterval(t)
  }, [pending, project])

  if (!project) return null
  const f = project.facts
  const props = proposals(f)

  // Toute question — saisie libre ou suggestion — va à Ti-Coune avec les FAITS MESURÉS ; si le modèle est
  // indisponible ou que sa réponse est écartée par les garde-fous, on montre l'état mesuré déterministe.
  async function ask(intent: ReturnType<typeof intentOf>, question: string) {
    if (thinking) return
    // Seuls les échanges réels vont au modèle : jamais l'état affiché à l'ouverture ni un repli déterministe,
    // qui peuvent dater d'avant un changement (ex. test automatique programmé depuis).
    const history = messages.filter(m => m.text).map(m => ({ from: m.from, text: m.text! }))
    setMessages(m => [...m, { from: 'user', text: question }])
    setThinking(true)
    let reply: Message
    try {
      const res = await fetch('/api/assistant/project', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, factsSheet: factsSheet(f), history, actions: props.map(p => p.title) }),
      })
      const body = await res.json().catch(() => null)
      if (res.ok && body?.mode === 'llm' && typeof body.text === 'string') {
        reply = { from: 'ai', text: body.text, llm: true, usedUserFacts: body.usedUserFacts ?? 0, graph: body.graph }
      } else {
        const code = body?.code ?? body?.error ?? `HTTP_${res.status}`
        const detail = body?.detail ? ` — ${body.detail}` : ''
        reply = { from: 'ai', answer: answer(intent, f), note: `Ti-Coune n’a pas pu répondre avec le modèle interne. Cause : ${code}${detail}.` }
      }
    } catch {
      reply = { from: 'ai', answer: answer(intent, f), note: 'Ti-Coune n’a pas pu répondre avec le modèle interne. Cause : PANEL_UNREACHABLE (le serveur web ne répond pas).' }
    }
    setThinking(false)
    setMessages(m => [...m, reply])
  }
  function submit() {
    const q = input.trim()
    if (!q) return
    setInput('')
    void ask(intentOf(q), q)
  }
  async function execute(p: Proposal) {
    // Validation explicite : le texte dit ce que l'action fait ET ce qu'elle ne fait pas.
    if (!confirm(p.confirmText)) return
    const run: ActionRun = { id: p.id, title: p.title, since: Date.now() }
    setRuns(r => [...r, run])
    try { await project!.run(p.id) } catch (e) {
      setRuns(r => r.map(x => (x === run ? { ...x, error: (e as Error).message } : x)))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: mono, paddingTop: 10 }}>
      {/* Ce que le panneau regarde */}
      <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 10px', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>CE QUE JE REGARDE</div>
        <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>{f.name}</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={chip('#93c5fd')}>{f.typeLabel}</span>
          {f.agents.map(a => <span key={a} style={chip('#94a3b8')}>{a}</span>)}
          {f.capabilities.map(c => <span key={c} style={chip('#34d399')}>{c}</span>)}
        </div>
        <button onClick={() => setShowFacts(s => !s)} style={{ marginTop: 8, background: 'none', border: 'none', color: '#93c5fd', fontSize: 10, cursor: 'pointer', padding: 0, fontFamily: mono }}>
          {showFacts ? '▾ masquer les faits utilisés' : '▸ faits utilisés'}
        </button>
        {showFacts && <FactList facts={f} />}
      </div>

      {/* Conversation */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.map((m, i) => m.from === 'user'
          ? <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '90%', fontSize: 11, color: 'var(--text-primary)', background: 'rgba(79,124,255,0.14)', border: '1px solid rgba(79,124,255,0.25)', borderRadius: 8, padding: '6px 9px' }}>{m.text}</div>
          : (
            <div key={i} style={{ fontSize: 11, lineHeight: 1.55, color: 'rgba(255,255,255,0.78)', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '8px 10px' }}>
              {m.llm
                ? m.text!.split(/\n{2,}/).map((p, k) => <p key={k} style={{ margin: k ? '6px 0 0' : 0, whiteSpace: 'pre-wrap' }}>{p}</p>)
                : (m.live ? answer('status', f) : m.answer!).paragraphs.map((p, k) => <p key={k} style={{ margin: k ? '6px 0 0' : 0 }}>{p}</p>)}
              {m.llm && (
                <div style={{ marginTop: 8, fontSize: 9, color: 'rgba(255,255,255,0.28)' }}>
                  Ti-Coune · faits mesurés du projet
                  {m.usedUserFacts ? ` · ${m.usedUserFacts} fait${m.usedUserFacts > 1 ? 's' : ''} de votre mémoire` : ' · aucun fait de votre mémoire'}
                  {m.graph?.edges
                    ? ` · ${m.graph.edges} lien${m.graph.edges > 1 ? 's' : ''} du graphe territorial${m.graph.unverified ? ` (dont ${m.graph.unverified} à vérifier)` : ' (sourcés)'}`
                    : ' · aucun lien pertinent dans le graphe'}
                  {' · '}<a href="/connaissance" style={{ color: '#93c5fd' }}>Connaissance</a>
                </div>
              )}
              {m.note && <div style={{ marginTop: 8, fontSize: 9, color: '#fcd34d' }}>{m.note} Voici néanmoins l’état mesuré du projet, sans reformulation : le modèle n’a pas répondu, les données du projet, elles, sont connues.</div>}
              {m.answer && m.answer.basis.length > 0 && <div style={{ marginTop: 8, fontSize: 9, color: 'rgba(255,255,255,0.28)' }}>Basé sur : {m.answer.basis.join(' · ')}</div>}
            </div>
          ))}
        {thinking && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Ti-Coune réfléchit…</div>}
        <div ref={endRef} />
      </div>

      {/* Propositions : jamais exécutées sans validation */}
      {props.length > 0 && (
        <div style={{ border: '1px solid rgba(252,211,77,0.3)', borderRadius: 8, padding: '8px 10px', background: 'rgba(252,211,77,0.04)' }}>
          <div style={{ fontSize: 9, letterSpacing: '0.12em', color: '#fcd34d', marginBottom: 6 }}>JE PROPOSE — RIEN NE PART SANS VOTRE VALIDATION</div>
          {props.map(p => (
            <div key={p.id} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-primary)' }}>{p.title}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', margin: '2px 0 5px' }}>Pourquoi : {p.why}</div>
              <button onClick={() => execute(p)} style={{ fontSize: 10, fontFamily: mono, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: '#34d399', border: '1px solid #34d39955' }}>Valider…</button>
            </div>
          ))}
        </div>
      )}

      {/* Exécution → vérification */}
      {runs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {runs.map((r, i) => {
            const v = verification(r.id, f, r.since)
            const color = r.error ? '#fca5a5' : v.done ? (v.ok ? '#34d399' : '#fca5a5') : '#fcd34d'
            return (
              <div key={i} style={{ fontSize: 10, color, border: `1px solid ${color}44`, borderRadius: 8, padding: '6px 9px', background: `${color}0d` }}>
                <strong>{r.title}</strong><br />{r.error ? `Refusé : ${r.error}` : v.text}
              </div>
            )
          })}
        </div>
      )}

      {/* Questions suggérées + saisie */}
      <div>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', marginBottom: 5 }}>QUESTIONS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {SUGGESTED.filter(s => f.backup || !['why_alert', 'last_test', 'rto_rpo', 'three_two_one', 'recovery'].includes(s.intent)).map(s => (
            <button key={s.intent} onClick={() => void ask(s.intent, s.label)} style={{ textAlign: 'left', fontSize: 10, fontFamily: mono, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>{s.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit() }} placeholder="Posez n’importe quelle question sur ce projet…"
            style={{ flex: 1, padding: '6px 9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 11, outline: 'none', fontFamily: mono }} />
          <button onClick={submit} style={{ fontSize: 10, fontFamily: mono, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: '#93c5fd', border: '1px solid #93c5fd55' }}>Demander</button>
        </div>
      </div>

      <div style={{ fontSize: 9, lineHeight: 1.6, color: 'rgba(255,255,255,0.25)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8 }}>
        Ti-Coune répond avec l’état MESURÉ de ce projet et ce que vous lui avez appris. Les faits mesurés font foi : une réponse qui invente un chiffre, déclare une conformité ou prétend avoir agi est écartée, et vous voyez alors l’état mesuré brut.
        Une question ne modifie rien ; toute action est proposée, validée par vous, exécutée, puis vérifiée.
      </div>
    </div>
  )
}

function FactList({ facts: f }: { facts: ProjectFacts }) {
  const b = f.backup
  const rows: [string, string][] = [
    ['Projet', f.name], ['Type', f.typeLabel], ['État', f.status], ['Agent(s)', f.agents.join(', ') || '—'], ['Dossier(s)', f.folders.join(', ') || '—'],
  ]
  if (b?.hasSet) {
    rows.push(
      ['Dernière passe', b.lastPass ? `${b.lastPass.status ?? '—'}${b.lastPass.stale ? ' (ancienne)' : ''}` : 'non remontée'],
      ['Copies vérifiées', b.protection ? `${b.protection.actual} / ${b.protection.target}` : 'non mesuré'],
      ['3-2-1', b.threeTwoOne?.summary ?? 'aucun instantané complet vérifié'],
      ['Phrase de récupération', b.recovery.configured ? 'confirmée' : 'en attente'],
      ['Test automatique', b.schedule.everyDays ? `tous les ${b.schedule.everyDays} jours (${b.schedule.state})` : 'manuel seulement'],
      ['Dernier test', b.lastTest ? b.lastTest.status : 'aucun'],
    )
    const t = b.lastTest
    if (t && t.status === 'PASSED') rows.push(
      ['Fichiers restaurés', `${t.filesRestored} / ${t.filesExpected}`], ['Empreintes confirmées', `${t.filesVerified} / ${t.filesExpected}`],
      ['Blocs vérifiés', `${t.blocksVerified} / ${t.blocksTotal}`], ['RTO observé', t.durationMs !== null ? `${t.durationMs} ms` : 'non mesuré'],
      ['RPO observé', t.rpoSeconds !== null ? `${t.rpoSeconds} s` : 'non mesuré'], ['Originaux modifiés pendant le test', t.originalsModified ? 'oui' : 'non'],
    )
  }
  return (
    <div style={{ marginTop: 6 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 8, fontSize: 10, padding: '1px 0' }}>
          <span style={{ color: 'rgba(255,255,255,0.35)', width: 120, flex: '0 0 auto' }}>{k}</span>
          <span style={{ color: 'rgba(255,255,255,0.75)' }}>{v}</span>
        </div>
      ))}
    </div>
  )
}
